import {SwaggerUIBundle} from 'swagger-ui-dist';
import CryptoJS from 'crypto-js';
import {showToast} from './toast';
//import { CopyToClipboard } from '../_components/clipboard';
import {trans} from '../i18n/i18n';
import {Toast} from "../consts/toast";
import {route} from "ziggy-js";

/**
 * Represents the SwaggerUIEnhancer class.
 * This class enhances the functionality of Swagger UI by adding customizations and event handling.
 */
class SwaggerUIEnhancer {

  // ======================================
  // === Constructor and Initialization ===
  // ======================================

  /**
   * Represents the SwaggerUIEnhancer class.
   * @constructor
   */
  constructor() {
    if (!SwaggerUIEnhancer.instance) {
      this.initialize();
      SwaggerUIEnhancer.instance = this;
    }

    return SwaggerUIEnhancer.instance;
  }

  /**
   * Initializes the Swagger UI Enhancer.
   */
  initialize() {
    const vdConfigData = document.getElementById('vd-config-data') || {};
    const swaggerConfigData = document.getElementById('swagger-config-data') || {};

    this.locale = vdConfigData.dataset.locale || 'en';
    this.apiKey = swaggerConfigData.dataset.apiKey || '';
    this.secretKey = swaggerConfigData.dataset.secretKey || '';
    this.apiVersion = (swaggerConfigData.dataset.apiVersion || 'latest');
    this.computableFields = ['API_KEY', 'CONTROL_SUM'];
    this.hashMappings = {
      '#examples-decode': '#operations-VIN_Decoder-VIN_Decode',
      '#examples-vehicle-market-value': '#operations-Vehicle_Market_Value-Vehicle_Market_Value',
      '#toc-vehicle-market-value-request': '#operations-Vehicle_Market_Value-Vehicle_Market_Value',
      '#examples-stolen': '#operations-Stolen_Check-Stolen_Check',
    };
    this.subMenuIds = this.loadSubMenuIds();
    this.adjustLanguageSettings();
    this.registerEvents();
    this.initSwagger();
    this.setupSwaggerObserver();
  }

  // it is necessary to keep language when navigate by link in the swagger ui
  /**
   * Adjusts the language settings based on the current locale.
   */
  adjustLanguageSettings() {
    if (this.locale === 'cs') {
      this.locale = 'cz';
    }
    if (this.locale === 'en') {
      this.locale = null;
    }
  }

  /**
   * Retrieves an array of sub-menu IDs from the sidebar links.
   * @returns {string[]} An array of sub-menu IDs.
   */
  loadSubMenuIds() {
    const links = document.querySelectorAll('aside .sidebar-link');
    return Array.from(links).map(link => this.removeHashFromId(link.dataset.id)).filter(id => id);
  }

  // ======================================
  // === Event Handlers ===
  // ======================================

  /**
   * Registers event listeners for the Swagger UI Enhancer.
   * @returns {void}
   */
  registerEvents() {
    this.removeEventListeners();
    window.addEventListener('vdNavigated', (event) => this.navigated(event));
    window.addEventListener('hashchange', (event) => this.handleHashChange(event));
    
    window.addEventListener('swaggerUILoaded', () => this.handleSwaggerUILoaded());
  }

  /**
   * Removes event listeners and disconnects the MutationObserver.
   */
  removeEventListeners() {
    // Remove event listeners
    window.removeEventListener('vdNavigated', () => this.navigated());
    window.removeEventListener('hashchange', () => this.handleHashChange());
    window.removeEventListener('swaggerUILoaded', () => this.handleSwaggerUILoaded());

    // Disconnect the MutationObserver
    if (this.swaggerObserver) {
      this.swaggerObserver.disconnect();
    }
  }

  /**
   * Handles Swagger UI load: checks and updates URL hash if present.
   * Updates URL based on `hashMappings` and dispatches a re-navigation event.
   * Expands sections on load.
   */
  handleSwaggerUILoaded() {
    if (window.location.hash) {

      const currentHash = window.location.hash;
      if (this.hashMappings[currentHash]) {
        history.pushState(null, null, this.hashMappings[currentHash]);
      }

      const renavigateReuestEvent = new CustomEvent('vdRenavigateRequested', {});
      window.dispatchEvent(renavigateReuestEvent);
    }
    
    this.expandSectionsOnLoad();
  }

  expandSectionsOnLoad() {
    const selectors = [
      '#model-VIN-Decode-Variable-Labels-List',
    ];

    selectors.forEach(selector => {
      const section = document.querySelector(selector);
      if (section && !this.isSectionOpen(section)) {
        const button = section.querySelector('button.model-box-control');
        if (button) {
          this.triggerButtonClick(button);
        }
      }
    });
  }

  /**
   * Handles the change in the hash of the window location.
   * If the hash corresponds to a valid element in the DOM, it opens the corresponding section.
   * If the hash corresponds to a sidebar link, it opens the parent section of the link.
   */
  handleHashChange(event) {
    event.preventDefault();
  }

  /**
   * Responds to navigation events within Swagger UI by handling focus on specific sections.
   * It uses a 'targetId' from the event detail to find and open the relevant UI section.
   */
  navigated(event) {
    const targetId = event.detail?.targetId;
    if (!targetId) {
      return;
    }
    const sidebarLink = document.querySelector(`[data-id="${this.ensureIdHasHash(targetId)}"]`);
    this.handleSectionOpen(targetId, sidebarLink, false);
  }

  /**
   * Handles click events on various elements within the Swagger section.
   * This method routes different click actions to specific handlers based on the type of button or link clicked.
  */
  handleSwaggerSectionClick(event) {
    if (event.target.matches('.btn.try-out__btn:not(.cancel)')) {
      this.onTryButtonClicked(event.target.closest('.opblock-section'));
    } else if (event.target.matches('.btn.try-out__btn.cancel')) {
      this.onCancelClicked(event.target.closest('.opblock-section'));
    } else if (event.target.matches('.api-manual-edition:not(.pen-square-checked)')) {
      this.onEditClicked(event.target.closest('td'));
    } else if (event.target.matches('.api-manual-edition.pen-square-checked')) {
      this.onEndEditClicked(event.target.closest('td'));
    }
  }

  /**
   * Handles the input event for the Swagger section.
   *
   * @param {Event} event - The input event.
   */
  handleSwaggerSectionInput(event) {
    // Check if the input is within a VIN parameter row
    if (event.target.closest('tr[data-param-name="VIN"]') && event.target.tagName === 'INPUT') {
      // Capitalize VIN input automatically
      const input = event.target;
      if (input && input.value) {
        const cursorPosition = input.selectionStart;
        const capitalizedValue = input.value.toUpperCase();
        if (input.value !== capitalizedValue) {
          input.value = capitalizedValue;
          // Restore cursor position after value change
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      }
      this.onVinCodeChanged(event.target.closest('.opblock-section'));
    } else if (event.target.closest('tr[data-param-name="API_KEY"]') && event.target.tagName === 'INPUT') {
      this.onVinCodeChanged(event.target.closest('.opblock-section'));
    }
  }

  /**
   * Handles the paste event for the Swagger section.
   *
   * @param {Event} event - The paste event.
   */
  handleSwaggerSectionPaste(event) {
    // Check if the paste is into a VIN parameter input
    if (event.target.closest('tr[data-param-name="VIN"]') && event.target.tagName === 'INPUT') {
      // Use setTimeout to wait for the paste to complete, then capitalize the value
      setTimeout(() => {
        const input = event.target;
        if (input && input.value) {
          const cursorPosition = input.selectionStart;
          const capitalizedValue = input.value.toUpperCase();
          if (input.value !== capitalizedValue) {
            input.value = capitalizedValue;
            // Restore cursor position after value change
            input.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
        this.onVinCodeChanged(event.target.closest('.opblock-section'));
      }, 0);
    }
  }

  /**
 * Handles the click event on a clipboard button.
 * This function attempts to retrieve the text from a React component's properties
 * and passes it to the clipboard function.
  * Note: This approach relies on accessing React's internal properties directly from the DOM,
  * which is generally discouraged and could be considered a hack. Such access is not guaranteed
  * to be stable across different versions of React, and could break if React's internal structure changes.
 * @param {HTMLElement} button - The button element that was clicked.
 */
  handleClipboardButtonClick(button) {
    const key = Object.keys(button).find(key => key.startsWith('__reactProps$'));
    if (key && button[key]) {
      const text = button[key].children.props.text;
      this.copyToClipboard(text);
    } else {
      showToast(trans("Failed to copy to clipboard"), Toast.ERROR);
    }
  }

  // ======================================
  // === Swagger UI Setup ===
  // ======================================

  /**
   * Initializes the Swagger UI.
   */
  initSwagger() {
    let url;
    
    switch(this.apiVersion) {      
      case '3.2':
        url = route('api.openapi.3.2');
        break;
      case 'latest':
        url = route('api.openapi.latest');
        break;
      default:
        url = route('api.openapi.latest');
        console.warn(lang('Unexpected API version: ' + this.apiVersion));
    }
    
    const supportedSubmitMethods = (!this.apiKey) ? [] : ['get'];
    window.ui = SwaggerUIBundle({
      url: url,
      dom_id: '#swagger-ui',
      deepLinking: false,
      presets: [
        SwaggerUIBundle.presets.apis
      ],
      plugins: [
        SwaggerUIBundle.plugins.DownloadUrl
      ],
      layout: "BaseLayout",
      supportedSubmitMethods: supportedSubmitMethods,
      onComplete: function () {
        window.dispatchEvent(new CustomEvent('swaggerUILoaded'));
      }
    });
  }

  /**
   * Dispatches an event to notify other parts of the application that a specific Swagger section has been updated.
   * This method creates a custom event 'vdSwaggerSectionUpdated' with details containing the 'sectionId' of the updated section.
   */
  dispatchSwaggerSectionUpdated(sectionId) {
    const event = new CustomEvent('vdSwaggerSectionUpdated', {
      detail: { sectionId: sectionId }
    });
    window.dispatchEvent(event);
  }

  /**
   * Sets up a MutationObserver to monitor changes in the Swagger UI element.
   */
  setupSwaggerObserver() {
    const swaggerElement = document.getElementById('swagger-ui');
    if (swaggerElement) {
      this.swaggerObserver = new MutationObserver((mutations) => this.observerCallback(mutations));
      this.swaggerObserver.observe(swaggerElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  /**
   * Callback function for the MutationObserver.
   * @param {MutationRecord[]} mutationsList - An array of MutationRecord objects representing the changes to the DOM.
   */
  observerCallback(mutationsList) {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.attachSwaggerListeners(node);
            this.onSwaggerMutationChildList(node);
          }
        });
        mutation.removedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.detachSwaggerListeners(node);
          }
        });
      }
      else if (mutation.type === 'attributes') {
        this.onSwaggerMutationAttributes(mutation.target);
      }
    }
  }

  /**
   * Attaches event listeners to the Swagger section nodes.
   *
   * @param {Node} node - The parent node containing the Swagger section nodes.
   */
  attachSwaggerListeners(node) {
    const opBlocks = node.querySelectorAll('.opblock-tag-section');
    opBlocks.forEach(opBlock => {
      opBlock.addEventListener('click', (event) => this.handleSwaggerSectionClick(event));
      opBlock.addEventListener('input', (event) => this.handleSwaggerSectionInput(event));
      opBlock.addEventListener('paste', (event) => this.handleSwaggerSectionPaste(event));
    });

    const clipboardButtons = node.querySelectorAll('.copy-to-clipboard');
    clipboardButtons.forEach(button => {
      button.addEventListener('click', (event) => this.handleClipboardButtonClick(button));
    });
  }

  /**
   * Detaches event listeners from Swagger section nodes.
   *
   * @param {Node} node - The node containing Swagger section elements.
   */
  detachSwaggerListeners(node) {
    const opBlocks = node.querySelectorAll('.opblock-tag-section');
    opBlocks.forEach(opBlock => {
      opBlock.removeEventListener('click', (event) => this.handleSwaggerSectionClick(event));
      opBlock.removeEventListener('input', (event) => this.handleSwaggerSectionInput(event));
      opBlock.removeEventListener('paste', (event) => this.handleSwaggerSectionPaste(event));
    });

    const clipboardButtons = node.querySelectorAll('.copy-to-clipboard');
    clipboardButtons.forEach(button => {
      button.removeEventListener('click', (event) => this.handleClipboardButtonClick(button));
    });
  }

  /**
   * Handles the mutation of a Swagger child list.
   *
   * @param {Node} node - The node representing the Swagger child list.
   */
  onSwaggerMutationChildList(node) {

    // hide swagger header
    const swaggerHeaders = node.querySelectorAll(".information-container:not(.d-none)");
    swaggerHeaders.forEach(header => header.classList.add('d-none'));

    // Language links fix
    if (this.locale) {
      node.querySelectorAll('a[href^="/api/"]').forEach(link => {
        const currentHref = link.getAttribute('href');
        const newHref = currentHref.replace('/api/', `/${this.locale}/api/`);
        link.setAttribute('href', newHref);
      });
    }

    // Add sidebar section to all swagger tag (group) elements
    node.querySelectorAll('.opblock-tag').forEach(tag => {
      tag.classList.add('sidebar-section', 'swagger-section-tag');
      this.dispatchSwaggerSectionUpdated(tag.id);
    });

    // Add sidebar section to selected swagger endpoints
    node.querySelectorAll('.opblock').forEach(opblock => {
      if (this.subMenuIds.includes(opblock.id)) {
        opblock.classList.add('sidebar-section', 'swagger-section-endpoint');
        this.dispatchSwaggerSectionUpdated(opblock.id);
      }
    });

    // Add special class to model-box elemet renderedMarkdown
    node.querySelectorAll('.model-box .renderedMarkdown').forEach(box => {
      box.classList.add('model-schema');
    });

    // Add class to identify lists in the swagger scheme
    document.querySelectorAll('.model-container[id$="-List"]').forEach(element => {
      element.classList.add('sidebar-section', 'swagger-section-list', 'list-model');
      const hasData = element.querySelector('table.model tbody')?.children.length > 0;
      if (hasData) {
        this.dispatchSwaggerSectionUpdated(element.id);
      }
    });

    // Add maxlength="17" attribute to VIN input fields
    node.querySelectorAll('tr[data-param-name="VIN"] input').forEach(vinInput => {
      if (!vinInput.hasAttribute('maxlength')) {
        vinInput.setAttribute('maxlength', '17');
      }
    });

    // Add sidebar section to section with class models
    node.querySelectorAll('section.models').forEach(section => {
      section.classList.add('sidebar-section', 'swagger-section-schames');
      section.id = 'api-data-models';
      this.dispatchSwaggerSectionUpdated(section.id);
    });

    // Add edit buttons
    node.querySelectorAll('tr[data-param-name]').forEach(tr => {
      const dataParamName = tr.dataset.paramName;
      if (this.computableFields.includes(dataParamName)) {
        const input = tr.querySelector('input[type="text"]');
        if (input && !input.classList.contains('api-computable')) {
          input.classList.add('api-computable');
          this.addEditButton(input.parentElement);
        }
      }
    });

    // Add copy to clipboard buttons
    const selectorMap = {
      '.request-url': 'microlight',
      '.highlight-code': 'microlight',
    };
    Object.keys(selectorMap).forEach(selector => {
      node.querySelectorAll(selector).forEach(nodeToCopyClipboard => {
        this.addCopyToClipboardNode(nodeToCopyClipboard, selectorMap[selector]);
      });
    });

  }
  /**
   * Adds a copy-to-clipboard button to the specified node.
   * This function dynamically creates a button element and appends it to the node
   * immediately after a specified child element (typically an <h4> element).
   * Clicking this button triggers text copying from a designated part of the node.
   * @param {HTMLElement} node - The DOM node to which the copy-to-clipboard button will be added.
   * @param {string} nodeFromCopyData - The class name of the element within the node from which text will be copied.
   */
  addCopyToClipboardNode(node, nodeFromCopyData) {
    const h4 = node.querySelector('h4');
    if (h4) {
      const newDiv = document.createElement('div');
      newDiv.className = 'copy-to-clipboard';
      const button = document.createElement('button');
      button.addEventListener('click', () => {
        const contentToCopy = node.querySelector(`.${nodeFromCopyData}`).textContent;
        this.copyToClipboard(contentToCopy);
      });
      newDiv.appendChild(button);
      h4.insertAdjacentElement('afterend', newDiv);
    }
  }

  /**
   * Copies the specified text to the clipboard.
   * 
   * @param {string} text - The text to be copied to the clipboard.
   */
  copyToClipboard(text) {
    navigator.clipboard.writeText(text)
      .then(() => {
        showToast(trans("Successfully copied to clipboard"), Toast.SUCCESS);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        showToast(trans("Failed to copy to clipboard"), Toast.ERROR);
      });
  }

  /**
   * Handles the mutation attributes for Swagger UI.
   *
   * @param {HTMLElement} target - The target element to be checked and modified.
   */
  onSwaggerMutationAttributes(target) {
    // Check and add 'sidebar-section' to '.opblock-tag' if not already present
    if (target.classList.contains('opblock-tag') && !target.classList.contains('sidebar-section')) {
      target.classList.add('sidebar-section', 'swagger-section-tag');
      this.dispatchSwaggerSectionUpdated(target.id);
    }
    const opblockTagElements = target.querySelectorAll('.opblock-tag');
    if (opblockTagElements.length > 0) {
      opblockTagElements.forEach(element => {
        if (!element.classList.contains('sidebar-section')) {
          element.classList.add('sidebar-section', 'swagger-section-tag');
        }
        this.dispatchSwaggerSectionUpdated(element.id);
      });
    }

    // Check and add 'sidebar-section' to '.opblock' based on certain conditions
    if (target.classList.contains('opblock') && !target.classList.contains('sidebar-section') && this.subMenuIds.includes(target.id)) {
      target.classList.add('sidebar-section', 'swagger-section-endpoint');
      this.dispatchSwaggerSectionUpdated(target.id);
    }
    // Check and add 'sidebar-section' to lists in the swagger scheme
    if (target.tagName.toUpperCase() === 'SECTION' && target.classList.contains('models')) {
      if (!target.classList.contains('sidebar-section')) {
        target.classList.add('sidebar-section', 'swagger-section-schames');
        target.id = 'api-data-models';
      }
      this.dispatchSwaggerSectionUpdated(target.id);
    }
    // Add class to identify lists in the swagger scheme
    if (target.classList.contains('model-container') && target.id.endsWith('-List') && !target.classList.contains('sidebar-section')) {
      target.classList.add('sidebar-section', 'swagger-section-list', 'list-model');
      this.dispatchSwaggerSectionUpdated(target.id);
    }
  }

  // =================================================================
  // === UI Manipulation ===
  // =================================================================

  /**
   * Opens a specific section in the Swagger UI based on the provided target ID.
   * This function checks if the target element is open, if not, it will attempt to open it,
   * managing both direct targets and their parent elements if necessary.
   * 
   * @param {string} targetId - The ID of the target element to be focused on.
   * @param {HTMLElement} sidebarLink - The sidebar link associated with the target element.
   * @param {boolean} navigate - Determines whether navigation to the target element should be triggered after opening.
   */
  handleSectionOpen(targetId, sidebarLink, navigate = true) {
    
    // Retrieve the target element using its ID, ensuring it includes a hash prefix
    const targetElement = document.querySelector(this.ensureIdHasHash(targetId));

    // Block 1: If the target element is already open, navigate if requested
    if (targetElement && this.isSectionOpen(targetElement)) {
      if (navigate) {
        this.navigate(targetElement);
      }
      return;
    }

    // Utility function to setup an event listener that self-destructs after event capture or timeout
    const setupEventListener = (elementId, callback) => {
      const eventHandler = (event) => {
        if (event.detail.sectionId === elementId) {
          window.removeEventListener('vdSwaggerSectionUpdated', eventHandler);
          clearTimeout(timeoutId); // Cancel the timer after capturing the event
          callback();
        }
      };
      window.addEventListener('vdSwaggerSectionUpdated', eventHandler);

      // Timer for auto-destruction of the event listener if the event is not captured within 2 seconds
      const timeoutId = setTimeout(() => {
        window.removeEventListener('vdSwaggerSectionUpdated', eventHandler);
      }, 2000);
    };

    // Block 2: If the target element does not exist, attempt to open the parent element
    if (!targetElement && sidebarLink) {
      navigate = true;
      const parentDataId = this.getParentDataId(sidebarLink);
      if (parentDataId) {
        const parentElement = document.querySelector(parentDataId);
        if (parentElement) {
          if (!this.isSectionOpen(parentElement)) {
            // Set up event listener and try to open parent section if it's not open
            setupEventListener(parentElement.id, () => this.handleSectionOpen(targetId, null, navigate));
            this.triggerButtonClick(parentElement.querySelector('button[aria-expanded]'));

          } else {
            // The parent section is open but the target element does not exist,
            // indicating a potential configuration error in the sidebar menu setup.
            // This should not happen and needs investigation if encountered.
            this.navigate(parentElement);
          }
        }
      }
      return;
    }
  
    // Block 3: If the target element exists but is closed
    if (targetElement) {
      navigate = true;
      const button = targetElement.querySelector('button[aria-expanded]');
      if (button && button.getAttribute('aria-expanded') === 'false') {
        // Set up event listener and trigger button click if section is closed
        setupEventListener(targetElement.id, () => {
          if (this.isSectionOpen(targetElement)) {
            this.handleSectionOpen(targetId, null, navigate);  // Should loop back to block 1
          }
        });
        this.triggerButtonClick(button);
      }
    }
  }

/**
 * Dispatches a custom navigation event to navigate to the specified target element.
 * Ensures the target element has an ID and constructs the event with the target ID in its details.
 * The event allows for bubbling and can be cancelled.
 * 
 * @param {HTMLElement} targetElement - The element intended for navigation.
 */
  navigate(targetElement) {
    if (targetElement && targetElement.id) {
      const targetId = this.ensureIdHasHash(targetElement.id);
      const navigationEvent = new CustomEvent('vdNavigateTo', {
        detail: {
          targetId: targetId
        },
        bubbles: true,
        cancelable: true
      });
      document.dispatchEvent(navigationEvent);
    }
  }

  /**
   * Checks if a given section element within the Swagger UI is currently open.
   * Determines the open state based on the element's class and attributes.
   * 
   * @param {HTMLElement} sectionElement - The section element to check.
   * @returns {boolean} True if the section is open, otherwise false.
   */
  isSectionOpen(sectionElement) {

    if (!sectionElement) {
      return false;
    }

    if (sectionElement.classList.contains('swagger-section-tag')) {
      return sectionElement.dataset.isOpen === "true";
    }
  
    if (sectionElement.classList.contains('swagger-section-endpoint')) {
      return sectionElement.classList.contains('is-open');
    }
  
    if (sectionElement.classList.contains('swagger-section-schames')) {
      return sectionElement.classList.contains('is-open');
    }
  
    if (sectionElement.classList.contains('swagger-section-list')) {
      const button = sectionElement.querySelector('button.model-box-control');
      return button && button.getAttribute('aria-expanded') === "true";
    }
  
    return false;
  }

  /**
   * Adds an edit button to the specified node.
   *
   * @param {HTMLElement} node - The node to which the edit button will be added.
   */
  addEditButton(node) {
    const existingEditButton = node.querySelector('.api-manual-edition');
    if (!existingEditButton) {
      const inputFields = node.querySelectorAll("input.api-computable");

      inputFields.forEach(inputField => {
        const manualEditButton = document.createElement('span');
        manualEditButton.className = 'api-manual-edition pen-square d-inline-block align-middle ms-1';
        manualEditButton.title = 'Set to manual editing';

        manualEditButton.onclick = (typeof copySecretKey === 'function') ? copySecretKey : () => { console.warn('copySecretKey function not defined.'); };

        inputField.parentNode.insertBefore(manualEditButton, inputField.nextSibling);

        if (inputField.disabled) {
          manualEditButton.classList.add('d-none');
        }
      });
    }
  }

  /**
   * Shows the edit button for a given node and sets input fields to readonly.
   * @param {HTMLElement} node - The node to show the edit button for.
   */
  showEditButton(node) {
    const hiddenButtons = node.querySelectorAll('.api-manual-edition.d-none');
    hiddenButtons.forEach(button => button.classList.remove('d-none'));

    const inputFields = node.querySelectorAll("input.api-computable");
    inputFields.forEach(input => {
      input.setAttribute('readonly', "true");
    });

    hiddenButtons.forEach(button => {
      button.setAttribute('title', 'Set to manual editing');
    });

    this.computeValues(node);
  }

  /**
   * Hides the edit button and removes the readonly attribute from input fields.
   *
   * @param {Node} node - The node containing the edit button and input fields.
   */
  hideEditButton(node) {
    const shownButtons = node.querySelectorAll('.api-manual-edition:not(.d-none)');
    shownButtons.forEach(button => button.classList.add('d-none'));
    const inputFields = node.querySelectorAll("input.api-computable");
    inputFields.forEach(input => input.removeAttribute('readonly'));
  }

  /**
   * Handles the click event of the "Try" button.
   *
   * @param {Node} node - The node representing the button element.
   */
  onTryButtonClicked(node) {
    this.showEditButton(node);
  }

  /**
   * Hides the edit button for the specified node when the cancel button is clicked.
   *
   * @param {Node} node - The node for which to hide the edit button.
   */
  onCancelClicked(node) {
    const editButtons = node.querySelectorAll('.api-manual-edition.pen-square-checked');
    editButtons.forEach(editButton => {
      this.onEndEditClicked(editButton.closest('.opblock'));
    });
    this.hideEditButton(node);
  }

  /**
   * Handles the click event when the edit button is clicked.
   * @param {HTMLElement} node - The node representing the clicked button.
   */
  onEditClicked(node) {
    const shownButton = node.querySelector('.api-manual-edition:not(.pen-square-checked)');
    shownButton.classList.add('pen-square-checked');
    const inputField = node.querySelector("input.api-computable");
    inputField.removeAttribute("readonly");
    shownButton.setAttribute('title', 'Set to automatically calculate value');
  }

  /**
   * Handles the click event when the "End Edit" button is clicked.
   * @param {HTMLElement} node - The node representing the clicked button.
   */
  onEndEditClicked(node) {
    const shownButton = node.querySelector('.api-manual-edition.pen-square-checked');
    shownButton.classList.remove('pen-square-checked');
    const inputField = node.querySelector("input.api-computable");
    inputField.setAttribute('readonly', "true");
    shownButton.setAttribute('title', 'Set to manual editing');
    this.computeValues(node.closest('.opblock-get'));
  }

  /**
   * Handles the event when the VIN code is changed.
   *
   * @param {Node} node - The node representing the VIN code.
   */
  onVinCodeChanged(node) {
    this.computeValues(node);
  }

  /**
   * Retrieves the parent data ID of a sidebar link.
   *
   * @param {HTMLElement} sidebarLink - The sidebar link element.
   * @returns {string|null} - The parent data ID, or null if not found.
   */
  getParentDataId(sidebarLink) {

    let dataParentId = sidebarLink.dataset.parentId;
    if (dataParentId) {
      return this.ensureIdHasHash(dataParentId);
    }

    const parentUl = sidebarLink.closest('li').parentElement;
    const possibleParentLink = parentUl.previousElementSibling;
    const parentLink = possibleParentLink && possibleParentLink.classList.contains('sidebar-link')
      && possibleParentLink.hasAttribute('data-id')
      ? possibleParentLink : null;
    return parentLink ? parentLink.dataset.id : null;
  }

  // =============================================================
  // === Utility Methods ===
  // =============================================================

  /**
   * Computes the values for VIN, API_KEY, and CONTROL_SUM inputs.
   * @param {HTMLElement} node - The node containing the inputs.
   */
  computeValues(node) {
    const vinInput = node.querySelector('tr[data-param-name="VIN"] input');
    const apiKeyInput = node.querySelector('tr[data-param-name="API_KEY"] input');
    const controlSumInput = node.querySelector('tr[data-param-name="CONTROL_SUM"] input');

    const isApiKeyInputReadOnly = apiKeyInput.getAttribute('readonly') !== null;
    const isControlSumInputReadOnly = controlSumInput.getAttribute('readonly') !== null;

    if (!isApiKeyInputReadOnly && !isControlSumInputReadOnly) {
      return;
    }

    if (isApiKeyInputReadOnly && apiKeyInput && apiKeyInput.value !== this.apiKey) {
      this.triggerInputChange(apiKeyInput, this.apiKey);
    }

    if (isControlSumInputReadOnly) {
      const section = node.closest('.opblock-get');
      const operationPathElement = section.querySelector('.opblock-summary-path');
      const operationPath = operationPathElement ? operationPathElement.dataset.path : null;
      if (operationPath) {
        let parts = operationPath.split('/');
        let operationId = parts[4];
        const operationId2 = parts[5];
        if (operationId2 === 'info') {
          operationId = operationId2;
        } else if (operationId2 === 'value-list') {
          operationId = `${operationId2}-${parts[7]}`;
        } else if (operationId === 'oem' && operationId2 === 'history') {
          operationId = 'oem-history';
        }
        operationId = operationId.replace('.{format}', '');
        const vin = vinInput && vinInput.value ? vinInput.value.trim() : undefined;
        const apiKeyVal = apiKeyInput && apiKeyInput.value ? apiKeyInput.value : this.apiKey;
        const controlSum = this.calculateControlSum(vin, operationId, apiKeyVal, this.secretKey);
        this.triggerInputChange(controlSumInput, controlSum);
      }
    }
  }

  /**
   * Calculates the control sum for a VIN (Vehicle Identification Number).
   *
   * @param {string} vin - The VIN to calculate the control sum for.
   * @param {string} operationId - The operation ID.
   * @param {string} apiKey - The API key.
   * @param {string} secretKey - The secret key.
   * @returns {string} - The calculated control sum, truncated to the first 10 characters.
   */
  calculateControlSum(vin, operationId, apiKey, secretKey) {
    const stringToHash = vin ? `${vin}|${operationId}|${apiKey}|${secretKey}` : `${operationId}|${apiKey}|${secretKey}`;
    const hash = CryptoJS.SHA1(stringToHash).toString(CryptoJS.enc.Hex);
    return hash.substring(0, 10);
  }

  /**
   * Triggers an input change event on the specified element with the given value.
   * This is a hack to deal with React not properly handling programmatic changes to input values.
   * 
   * @param {HTMLElement} element - The input element to trigger the change event on.
   * @param {string} value - The value to set on the input element.
   */
  triggerInputChange(element, value) {
    const lastValue = element.value;
    element.value = value;
    const event = new Event('input', { bubbles: true });
    event.simulated = true;
    let tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(lastValue);
    }
    element.dispatchEvent(event);
  }

  /**
   * Triggers a click event on the specified element.
   * This is a hack to ensure that React's event system recognizes the event as being legitimate.
   *
   * @param {HTMLElement} element - The element on which to trigger the click event.
   */
  triggerButtonClick(element) {
    const event = new MouseEvent('click', {
      bubbles: true,
      cancelable: true
    });
    event.simulated = true;
    element.dispatchEvent(event);
  }

  /**
   * Adds a '#' prefix to an ID if it is not already present.
   * @param {string} id - The element's ID, which may or may not contain '#'.
   * @returns {string} The ID with a '#' prefix.
   */
  ensureIdHasHash(id) {
    return id.startsWith('#') ? id : `#${id}`;
  }

  /**
   * Removes the '#' prefix from an ID if it is present.
   * @param {string} id - The element's ID, which may or may not contain '#'.
   * @returns {string} The ID without the '#' prefix.
   */
  removeHashFromId(id) {
    return id.startsWith('#') ? id.slice(1) : id;
  }

}

document.addEventListener('DOMContentLoaded', () => {
  /**
   * Represents the sidebar aside element in the Swagger UI.
   * @type {HTMLElement}
   */
  const sidebarAside = document.getElementById('swagger-ui');
  if (sidebarAside) {
    new SwaggerUIEnhancer();
  }
});
